import { useState, useEffect, useCallback, useRef } from "react";
import type { Comment } from "@/mocks/articles";
import { initialComments } from "@/mocks/articles";

interface CommentSectionProps {
  articleId: string;
}

function formatTime(createdAt: string): string {
  const now = new Date("2026-06-29");
  const created = new Date(createdAt);
  const diffDays = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "今天";
  if (diffDays === 1) return "昨天";
  if (diffDays < 7) return `${diffDays}天前`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}周前`;
  return createdAt.slice(0, 10);
}

function formatLikes(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function generateAvatar(name: string): string {
  const colors = [
    "bg-accent-500", "bg-primary-500", "bg-secondary-500",
    "bg-amber-500", "bg-rose-500", "bg-emerald-500",
    "bg-cyan-500", "bg-orange-500",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorClass = colors[Math.abs(hash) % colors.length];
  const initials = name.slice(0, 1);
  return `${colorClass}|${initials}`;
}

function Avatar({ name }: { name: string }) {
  const [colorClass, initials] = generateAvatar(name);
  return (
    <div className={`w-8 h-8 rounded-full ${colorClass} flex items-center justify-center text-background-50 text-xs font-medium flex-shrink-0`}>
      {initials}
    </div>
  );
}

export default function CommentSection({ articleId }: CommentSectionProps) {
  const storageKey = `article_comments_${articleId}`;
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentLikes, setCommentLikes] = useState<Record<string, boolean>>({});
  const [commentLikeCounts, setCommentLikeCounts] = useState<Record<string, number>>({});
  const [showForm, setShowForm] = useState(false);
  const [formAuthor, setFormAuthor] = useState("");
  const [formContent, setFormContent] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed: Comment[] = JSON.parse(stored);
        setComments(parsed);
      } catch {
        const articleComments = initialComments.filter((c) => c.articleId === articleId);
        setComments(articleComments);
        localStorage.setItem(storageKey, JSON.stringify(articleComments));
      }
    } else {
      const articleComments = initialComments.filter((c) => c.articleId === articleId);
      setComments(articleComments);
      localStorage.setItem(storageKey, JSON.stringify(articleComments));
    }

    const storedLikes: Record<string, boolean> = {};
    const storedCounts: Record<string, number> = {};
    const allComments = initialComments.filter((c) => c.articleId === articleId);
    allComments.forEach((c) => {
      const likedKey = `cmt_liked_${c.id}`;
      const countKey = `cmt_like_count_${c.id}`;
      storedLikes[c.id] = localStorage.getItem(likedKey) === "true";
      const cnt = localStorage.getItem(countKey);
      storedCounts[c.id] = cnt !== null ? parseInt(cnt, 10) : c.likes;
    });
    setCommentLikes(storedLikes);
    setCommentLikeCounts(storedCounts);
  }, [articleId, storageKey]);

  const toggleCommentLike = useCallback((commentId: string) => {
    const likedKey = `cmt_liked_${commentId}`;
    const countKey = `cmt_like_count_${commentId}`;

    setCommentLikes((prev) => {
      const newLiked = !prev[commentId];
      localStorage.setItem(likedKey, String(newLiked));

      setCommentLikeCounts((prevCounts) => {
        const current = prevCounts[commentId] ?? 0;
        const delta = newLiked ? 1 : -1;
        const newCount = current + delta;
        localStorage.setItem(countKey, String(newCount));
        return { ...prevCounts, [commentId]: newCount };
      });

      return { ...prev, [commentId]: newLiked };
    });
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setFormError("");

      const trimmedAuthor = formAuthor.trim();
      const trimmedContent = formContent.trim();

      if (!trimmedAuthor) {
        setFormError("请输入昵称");
        return;
      }
      if (trimmedAuthor.length > 20) {
        setFormError("昵称不能超过20个字符");
        return;
      }
      if (!trimmedContent) {
        setFormError("请输入评论内容");
        return;
      }
      if (trimmedContent.length > 500) {
        setFormError("评论内容不能超过500个字符");
        return;
      }

      const honeypotInput = formRef.current?.querySelector<HTMLInputElement>('input[name="phone_alt"]');
      if (honeypotInput && honeypotInput.value.trim() !== "") {
        setSubmitSuccess(true);
        setFormAuthor("");
        setFormContent("");
        setShowForm(false);
        return;
      }

      setSubmitting(true);

      const newComment: Comment = {
        id: `cmt-local-${Date.now()}`,
        articleId,
        author: trimmedAuthor,
        authorAvatar: null,
        content: trimmedContent,
        createdAt: new Date().toISOString().slice(0, 16).replace("T", " "),
        likes: 0,
      };

      const updatedComments = [newComment, ...comments];
      setComments(updatedComments);
      localStorage.setItem(storageKey, JSON.stringify(updatedComments));

      fetch("https://readdy.ai/api/form/d915hci181e9e92u99g0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          author: trimmedAuthor,
          content: trimmedContent,
          articleId,
        }).toString(),
      })
        .then(() => {})
        .catch(() => {});

      setSubmitting(false);
      setSubmitSuccess(true);
      setFormAuthor("");
      setFormContent("");
      setShowForm(false);

      setTimeout(() => setSubmitSuccess(false), 3000);
    },
    [formAuthor, formContent, articleId, comments, storageKey],
  );

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-heading font-bold text-foreground-950 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-chat-3-line text-foreground-700"></i>
          </span>
          评论
          {comments.length > 0 && (
            <span className="text-sm font-normal text-foreground-400">({comments.length})</span>
          )}
        </h3>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-edit-line text-sm"></i>
            </span>
            写评论
          </button>
        )}
      </div>

      {submitSuccess && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-accent-100 text-accent-700 text-sm flex items-center gap-2">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-checkbox-circle-fill"></i>
          </span>
          评论发表成功！感谢你的参与。
        </div>
      )}

      {showForm && (
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          data-readdy-form
          className="mb-6 p-5 rounded-xl bg-background-100 border border-background-200/70"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-foreground-800">发表评论</span>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setFormError("");
              }}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-200 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>

          <div className="mb-3">
            <input
              type="text"
              name="author"
              placeholder="你的昵称"
              value={formAuthor}
              onChange={(e) => setFormAuthor(e.target.value)}
              maxLength={20}
              className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-300 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
            />
          </div>

          <div className="mb-3">
            <textarea
              name="content"
              placeholder="写下你的想法..."
              value={formContent}
              onChange={(e) => setFormContent(e.target.value)}
              maxLength={500}
              rows={4}
              className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-900 placeholder-foreground-300 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
            ></textarea>
            <div className="flex justify-end mt-1">
              <span className={`text-xs ${formContent.length > 500 ? "text-accent-500" : "text-foreground-300"}`}>
                {formContent.length}/500
              </span>
            </div>
          </div>

          <div className="form-honeypot-wrapper" aria-hidden="true">
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="honeypot-input"
            />
          </div>

          {formError && (
            <p className="text-accent-500 text-xs mb-3">{formError}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full px-4 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "发表中..." : "发表评论"}
          </button>
        </form>
      )}

      {comments.length === 0 && !showForm && (
        <div className="py-12 text-center">
          <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-foreground-300">
            <i className="ri-chat-off-line text-3xl"></i>
          </span>
          <p className="text-foreground-400 text-sm">暂无评论，来做第一个吃瓜群众吧！</p>
        </div>
      )}

      <div className="space-y-4">
        {comments.map((comment) => {
          const isLiked = !!commentLikes[comment.id];
          const displayLikes =
            commentLikeCounts[comment.id] !== undefined
              ? commentLikeCounts[comment.id]
              : comment.likes;

          return (
            <div
              key={comment.id}
              className="flex gap-3 p-4 rounded-xl bg-background-50 border border-background-100 hover:border-background-200/70 transition-colors"
            >
              <Avatar name={comment.author} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-foreground-900">{comment.author}</span>
                  <span className="text-xs text-foreground-300">{formatTime(comment.createdAt)}</span>
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed break-words">{comment.content}</p>
                <div className="flex items-center gap-4 mt-2">
                  <button
                    onClick={() => toggleCommentLike(comment.id)}
                    className="inline-flex items-center gap-1 text-xs cursor-pointer whitespace-nowrap transition-colors"
                    style={{ color: isLiked ? "oklch(var(--accent-500))" : "oklch(var(--foreground-300))" }}
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`${isLiked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} text-xs`}></i>
                    </span>
                    {displayLikes > 0 && <span>{formatLikes(displayLikes)}</span>}
                  </button>
                  <button
                    className="inline-flex items-center gap-1 text-xs text-foreground-300 cursor-pointer whitespace-nowrap hover:text-foreground-500 transition-colors"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-reply-line text-xs"></i>
                    </span>
                    回复
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}